/**
 * Side panel notification manager
 */

export class PanelManager {
    constructor() {
        this.panel = null;
        this.container = null;
        this.loadMoreBtn = null;
        this.currentOffset = 0;
        this.pageSize = 10;
        this.isLoading = false;
    }
    
    /**
     * Check if API requests are blocked via NotificationManager
     */
    _checkBlocked() {
        if (window.NotificationManager && window.NotificationManager.isBlocked) {
            console.warn('[PanelManager] API requests blocked due to 3 consecutive errors. Reload page to retry.');
            return true;
        }
        return false;
    }
    
    /**
     * Initialize panel
     */
    init() {
        this.panel = document.getElementById('notifications-panel');
        if (!this.panel) {
            this._createPanel();
        }
        
        this.container = this.panel.querySelector('[data-notifications-list]');
        this.loadMoreBtn = this.panel.querySelector('[data-load-more]');
        
        // Setup close button
        const closeBtn = this.panel.querySelector('[data-panel-close]');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
        }
        
        // Setup load more button
        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener('click', () => this.loadMore());
        }
        
        // Close on backdrop click
        this.panel.addEventListener('click', (e) => {
            if (e.target === this.panel) {
                this.close();
            }
        });
    }
    
    /**
     * Create panel if doesn't exist
     */
    _createPanel() {
        this.panel = document.createElement('div');
        this.panel.id = 'notifications-panel';
        this.panel.className = 'notifications-panel d-none';
        this.panel.innerHTML = `
            <div class="notifications-panel__header">
                <h5>Notifications</h5>
                <button class="btn-close" data-panel-close></button>
            </div>
            <div class="notifications-panel__content" data-notifications-list>
                <div class="notifications-loading">Loading...</div>
            </div>
            <div class="notifications-panel__footer">
                <button class="btn btn-link" data-load-more>Load 10 more</button>
            </div>
        `;
        document.body.appendChild(this.panel);
    }
    
    /**
     * Open panel
     */
    async open() {
        console.log('[PanelManager] Opening panel');
        this.panel.classList.remove('d-none');
        this.currentOffset = 0;
        this.container.innerHTML = '<div class="notifications-loading">Loading...</div>';
        await this.loadMore();
    }
    
    /**
     * Close panel
     */
    close() {
        this.panel.classList.add('d-none');
    }
    
    /**
     * Load more notifications
     */
    async loadMore() {
        if (this.isLoading) return;
        if (this._checkBlocked()) {
            this.container.innerHTML = '<div class="notifications-empty">API requests blocked. Please reload the page.</div>';
            return;
        }
        
        this.isLoading = true;
        
        try {
            console.log('[PanelManager] Loading notifications, offset:', this.currentOffset);
            const response = await fetch(`/api/notifications?limit=${this.pageSize}&offset=${this.currentOffset}&all=true`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            
            console.log('[PanelManager] Response data:', data);
            
            const notifications = data.data?.notifications || data.notifications || [];
            
            console.log('[PanelManager] Notifications count:', notifications.length);
            
            if (notifications.length === 0) {
                if (this.currentOffset === 0) {
                    this.container.innerHTML = '<div class="notifications-empty">No notifications</div>';
                } else {
                    this.loadMoreBtn.classList.add('d-none');
                }
                this.isLoading = false;
                return;
            }
            
            // Remove loading indicator
            const loading = this.container.querySelector('.notifications-loading');
            if (loading) loading.remove();
            
            // Add notifications
            notifications.forEach(notif => {
                console.log('[PanelManager] Creating notification item:', notif.id, notif.title);
                const item = this._createNotificationItem(notif);
                this.container.appendChild(item);
                // Notifications automatically marked as seen via API call
            });
            
            // Apply translations to newly added elements
            if (window.TranslationApplier && typeof window.TranslationApplier.apply === 'function') {
                window.TranslationApplier.apply();
            }
            
            this.currentOffset += notifications.length;
            
            // Hide load more if we got less than page size
            if (notifications.length < this.pageSize) {
                this.loadMoreBtn.classList.add('d-none');
            }
            
            // Reset error counter on success
            if (window.NotificationManager && window.NotificationManager._handleApiSuccess) {
                window.NotificationManager._handleApiSuccess();
            }
        } catch (error) {
            console.error('[PanelManager] Failed to load notifications:', error);
            this.container.innerHTML = '<div class="notifications-empty">Failed to load notifications</div>';
            
            // Handle error via NotificationManager
            if (window.NotificationManager && window.NotificationManager._handleApiError) {
                window.NotificationManager._handleApiError();
            }
        } finally {
            this.isLoading = false;
        }
    }
    
    /**
     * Create notification item element
     */
    _createNotificationItem(notif) {
        const item = document.createElement('div');
        item.className = 'notification-item';
        
        // Store user_id and dismissed flag
        item.dataset.userId = notif.user_id;
        item.dataset.dismissed = notif.dismissed;
        
        const bgClass = this._getBadgeClass(notif.type);
        
        // Show close button only for:
        // 1. Personal notifications (user_id > 0) AND not dismissed
        // 2. Never show for global notifications (user_id = -1)
        const showCloseBtn = notif.user_id > 0 && !notif.dismissed;
        
        // Check if this is a message reply notification (only for feedback/bug/feature messages)
        // System notifications like "Roles Updated" should not have reply button
        const messageId = this._extractMessageId(notif.message);
        const replyId = this._extractReplyId(notif.message);
        const isMessageReply = notif.title && (
            notif.title.toLowerCase().includes('reply to your') ||
            notif.title.toLowerCase().startsWith('new reply')
        );
        const showReplyBtn = messageId !== null && notif.user_id > 0 && isMessageReply;
        
        // Debug logging
        if (messageId !== null) {
            console.log('[PanelManager] Message notification detected:', {
                messageId,
                title: notif.title,
                isMessageReply,
                user_id: notif.user_id,
                showReplyBtn
            });
        }
        
        // Format message without the message_id and reply_id metadata
        const displayMessage = notif.message ? notif.message.replace(/\n\n__MESSAGE_ID__:\d+(\n__REPLY_ID__:\d+)?$/, '') : '';
        
        item.innerHTML = `
            <div class="notification-item__header">
                <span class="badge ${bgClass}">${notif.type}</span>
                <span class="text-muted small">${this._formatDate(notif.created_at)}</span>
                ${showCloseBtn ? `<button class="btn-close btn-sm ms-auto" data-dismiss-notif="${notif.id}"></button>` : ''}
            </div>
            <div class="notification-item__title">${this._escapeHtml(notif.title)}</div>
            <div class="notification-item__content">${this._formatMessage(displayMessage)}</div>
            ${showReplyBtn ? `<div class="notification-item__actions mt-2 d-flex gap-2">
                <a href="/messages/${messageId}" class="btn btn-sm btn-outline-secondary" onclick="window.open('/messages/${messageId}', '_blank', 'width=900,height=700'); return false;">
                    <i class="bi bi-eye"></i> <span data-translate="btn_view">Просмотреть</span>
                </a>
                <button class="btn btn-sm btn-primary" data-reply-message="${messageId}" data-reply-to="${replyId || ''}">
                    <i class="bi bi-reply"></i> <span data-translate="btn_reply">Ответить</span>
                </button>
            </div>` : ''}
        `;
        
        // Dismiss handler - only for personal notifications that are not dismissed
        const dismissBtn = item.querySelector(`[data-dismiss-notif="${notif.id}"]`);
        if (dismissBtn) {
            dismissBtn.addEventListener('click', () => {
                this.dismiss(notif.id, item);
            });
        }
        
        // Reply button handler
        const replyBtn = item.querySelector(`[data-reply-message]`);
        if (replyBtn) {
            replyBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const msgId = parseInt(replyBtn.getAttribute('data-reply-message'), 10);
                const replyToId = replyBtn.getAttribute('data-reply-to') ? parseInt(replyBtn.getAttribute('data-reply-to'), 10) : null;
                item.dataset.notificationId = notif.id;
                this._openReplyModal(msgId, notif.id, replyToId);
            });
        }
        
        // Store notification ID in item for dismissal
        item.dataset.notificationId = notif.id;
        
        return item;
    }
    
    /**
     * Dismiss personal notification
     */
    async dismiss(notificationId, itemElement) {
        if (this._checkBlocked()) return;
        
        try {
            const response = await fetch(`/api/notifications/${notificationId}/dismiss`, {
                method: 'POST'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            // Remove item from UI only after successful API call
            if (itemElement) {
                itemElement.remove();
            }
            
            // Update badge locally (decrease by 1) instead of separate API call
            if (window.NotificationManager) {
                if (window.NotificationManager.unreadCount > 0) {
                    window.NotificationManager.unreadCount--;
                    window.NotificationManager.updateBadge(window.NotificationManager.unreadCount);
                }
            }
            
            // Reset error counter on success
            if (window.NotificationManager && window.NotificationManager._handleApiSuccess) {
                window.NotificationManager._handleApiSuccess();
            }
        } catch (error) {
            console.error('[PanelManager] Failed to dismiss:', error);
            
            // Handle error via NotificationManager
            if (window.NotificationManager && window.NotificationManager._handleApiError) {
                window.NotificationManager._handleApiError();
            }
        }
    }
    
    /**
     * Get badge class by type
     */
    _getBadgeClass(type) {
        const map = {
            'info': 'bg-info',
            'success': 'bg-success',
            'warning': 'bg-warning',
            'error': 'bg-error'
        };
        return map[type] || 'bg-secondary';
    }
    
    /**
     * Format date
     */
    _formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
    
    /**
     * Escape HTML
     */
    _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    /**
     * Format message with line breaks
     */
    _formatMessage(text) {
        const escaped = this._escapeHtml(text);
        return escaped.replace(/\n/g, '<br>');
    }
    
    /**
     * Extract message_id and reply_id from notification message
     * Format: "...\n\n__MESSAGE_ID__:123\n__REPLY_ID__:456"
     */
    _extractMessageId(message) {
        if (!message) return null;
        const match = message.match(/__MESSAGE_ID__:(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }
    
    _extractReplyId(message) {
        if (!message) return null;
        const match = message.match(/__REPLY_ID__:(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }
    
    /**
     * Helper function to translate text
     */
    _translate(key) {
        if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
            const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
            const translation = window.TranslationRegistry.translate(key, lang);
            if (translation !== key) return translation;
        }
        return key;
    }
    
    /**
     * Create reply modal if doesn't exist
     */
    _ensureReplyModal() {
        let modalEl = document.getElementById('messageReplyModal');
        if (modalEl) return modalEl;
        
        const html = `
<div class="modal fade" id="messageReplyModal" tabindex="-1" aria-labelledby="messageReplyModalLabel" aria-hidden="true">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="messageReplyModalLabel" data-translate="messages_reply_modal_title">Ответить на сообщение</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" data-translate-aria-label="messages_close"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <label for="reply-message-text" class="form-label" data-translate="messages_reply_label">Ваш ответ</label>
          <textarea class="form-control" id="reply-message-text" rows="5" maxlength="5000" required data-translate-placeholder="messages_reply_placeholder"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" data-translate="feedback_modal_cancel">Отмена</button>
        <button type="button" class="btn btn-primary" id="reply-message-submit" data-translate="feedback_modal_send">Отправить</button>
      </div>
    </div>
  </div>
</div>`;
        
        const div = document.createElement('div');
        div.innerHTML = html;
        document.body.appendChild(div.firstElementChild);
        modalEl = document.getElementById('messageReplyModal');
        
        // Apply translations to modal (with small delay to ensure translations are loaded)
        setTimeout(() => {
            if (window.TranslationApplier && typeof window.TranslationApplier.apply === 'function') {
                window.TranslationApplier.apply();
            }
            // Ensure placeholder is set
            const textarea = modalEl.querySelector('#reply-message-text');
            if (textarea && !textarea.placeholder && window.TranslationRegistry) {
                const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                const placeholder = window.TranslationRegistry.translate('messages_reply_placeholder', lang);
                if (placeholder !== 'messages_reply_placeholder') {
                    textarea.placeholder = placeholder;
                }
            }
        }, 50);
        
        // Setup submit handler
        const submitBtn = modalEl.querySelector('#reply-message-submit');
        if (submitBtn) {
            submitBtn.addEventListener('click', async () => {
                const textarea = document.getElementById('reply-message-text');
                const messageId = modalEl.dataset.messageId;
                
                if (!textarea || !messageId) return;
                
                const content = (textarea.value || '').trim();
                if (!content) {
                    alert(this._translate('messages_reply_required'));
                    return;
                }
                
                submitBtn.disabled = true;
                submitBtn.textContent = this._translate('messages_sending');
                
                try {
                    const replyToId = modalEl.dataset.replyToId ? parseInt(modalEl.dataset.replyToId, 10) : null;
                    const response = await fetch(`/api/messages/${messageId}/reply`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ content: content, reply_to_reply_id: replyToId })
                    });
                    
                    const data = await response.json();
                    
                    if (!response.ok || !data.success) {
                        throw new Error(data.error || this._translate('messages_send_error'));
                    }
                    
                    // Close modal
                    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const modal = bootstrap.Modal.getInstance(modalEl);
                        if (modal) modal.hide();
                    }
                    
                    textarea.value = '';
                    
                    // Show success toast (reuse feedback modal toast function)
                    this._showSuccessToast(this._translate('messages_reply_sent'), this._translate('messages_reply_sent_success'));
                    
                    // Dismiss the notification that triggered this reply
                    const notificationId = modalEl.dataset.notificationId;
                    if (notificationId && window.NotificationManager && window.NotificationManager.dismiss) {
                        window.NotificationManager.dismiss(parseInt(notificationId, 10));
                        // Remove notification from UI if in panel
                        const notificationItem = document.querySelector(`[data-notification-id="${notificationId}"]`);
                        if (notificationItem) {
                            notificationItem.remove();
                        }
                    }
                    
                } catch (error) {
                    console.error('[PanelManager] Error sending reply:', error);
                    alert(this._translate('messages_send_error') + ': ' + error.message);
                } finally {
                    submitBtn.disabled = false;
                    submitBtn.textContent = this._translate('feedback_modal_send');
                }
            });
        }
        
        // Clear textarea when modal is hidden
        modalEl.addEventListener('hidden.bs.modal', function() {
            const textarea = modalEl.querySelector('#reply-message-text');
            if (textarea) textarea.value = '';
            delete modalEl.dataset.messageId;
        });
        
        return modalEl;
    }
    
    /**
     * Open reply modal
     */
    _openReplyModal(messageId, notificationId, replyToId = null) {
        if (!messageId) return;
        
        const modalEl = this._ensureReplyModal();
        modalEl.dataset.messageId = messageId;
        if (notificationId) {
            modalEl.dataset.notificationId = notificationId;
        }
        modalEl.dataset.replyToId = replyToId || '';
        modalEl.dataset.replyToId = replyToId || '';
        
        if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
            const modal = new bootstrap.Modal(modalEl);
            modal.show();
        }
    }
    
    /**
     * Show success toast (reuse feedback modal style)
     */
    _showSuccessToast(title, text) {
        // Reuse feedback modal toast function if available
        if (window.Messages && window.Messages.showSuccessToast) {
            window.Messages.showSuccessToast(title, text);
            return;
        }
        
        // Fallback: create toast manually
        const container = document.getElementById('notifications-toast-container') || 
                         document.getElementById('feedback-toast-container') ||
                         (() => {
                             const c = document.createElement('div');
                             c.id = 'feedback-toast-container';
                             c.className = 'position-fixed top-0 end-0 p-3';
                             c.style.zIndex = '1060';
                             c.style.maxWidth = '400px';
                             document.body.appendChild(c);
                             return c;
                         })();
        
        const id = 'toast-' + Date.now();
        const html = `
            <div id="${id}" class="toast mb-2 align-items-center border-0" role="status" aria-live="polite" aria-atomic="true" data-bs-delay="3800">
                <div class="toast-header bg-success text-canvas">
                    <i class="bi bi-check-circle-fill me-2"></i>
                    <strong class="me-auto">${this._escapeHtml(title || 'Success')}</strong>
                    <small>now</small>
                    <button type="button" class="btn-close btn-close-white ms-2 mb-1" data-bs-dismiss="toast" aria-label="Close"></button>
                </div>
                <div class="toast-body">
                    ${this._escapeHtml(text || '')}
                </div>
            </div>`;
        
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const toast = wrapper.firstElementChild;
        container.appendChild(toast);
        
        if (typeof bootstrap !== 'undefined' && bootstrap.Toast) {
            const bsToast = new bootstrap.Toast(toast);
            bsToast.show();
            toast.addEventListener('hidden.bs.toast', function() { toast.remove(); });
        } else {
            setTimeout(() => { if (toast && toast.parentNode) toast.remove(); }, 3800);
        }
    }
    
    /**
     * Cleanup
     */
    destroy() {
        // Nothing to cleanup
    }
}

